import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  List,
  Loader2,
  Phone,
  Utensils,
  Video,
  X,
} from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { formatPhoneToken as formatPhone } from '../boardUtils';
import { useFetchMeetings } from '../../../../services/onboardingBoard';
import { LegacyTag, LunchTag, OutlookTag, isLegacy, isLunch, isOutlook } from '../../scheduling/MeetingActions';

// The meetings calendar (Nic 2026-09-24): the Sales Rep now books their own
// meetings, so they need one place that answers "who, and when". Two views:
//   Agenda  today and the next two weeks, grouped by day, with Call now
//   Week    a 7-day time grid, the shape of a normal calendar
// Every time shows in the viewer's own local clock, like the rest of
// Potential Clients. Managers can switch to everyone's meetings.

const HOUR_PX = 52;

const nameOf = (m) =>
  isLunch(m)
    ? 'Lunch'
    : isOutlook(m)
      ? m.subject || 'Busy'
      : [m.leadFirstName, m.leadLastName].filter(Boolean).join(' ') || 'Unknown lead';

// The colors (Nic 2026-10-28): a meeting booked in the app wears its status,
// solid while ahead and a soft tint once it's over, so a busy week reads
// calm and the eye lands on what's next. The viewer's own Outlook
// appointment is grey with a dashed edge, faded once past. No icons in the
// blocks (the space is small); only the live dot.
const TONE = {
  upcoming: 'bg-sky-500 text-white',
  soon: 'bg-teal-500 text-white',
  now: 'bg-teal-500 text-white',
  met: 'bg-emerald-100 text-emerald-800 dark:!bg-emerald-500/20 dark:text-emerald-200',
  no_show: 'bg-rose-100 text-rose-700 dark:!bg-rose-500/20 dark:text-rose-200',
  canceled: 'bg-amber-100 text-amber-800 dark:!bg-amber-500/20 dark:text-amber-200',
  // Dark mode: no dashed edge (it read as a white outline, Nic 2026-10-28),
  // the grey fill alone.
  outlook: 'border border-dashed border-slate-400 bg-slate-100 text-slate-600 dark:!border-transparent dark:!bg-white/10 dark:text-slate-300',
  outlookPast: 'border border-dashed border-slate-300 bg-slate-50 text-slate-400 dark:!border-transparent dark:!bg-white/[0.05] dark:text-slate-500',
  // Lunch (Nic 2026-11-02): soft violet, faded once past.
  lunch: 'bg-violet-100 text-violet-800 dark:!bg-violet-500/20 dark:text-violet-200',
  lunchPast: 'bg-violet-50 text-violet-400 dark:!bg-violet-500/10 dark:text-violet-300/60',
};
// The agenda's chip, the same colors with the word.
const CHIP = {
  upcoming: 'bg-sky-50 text-sky-700 dark:!bg-sky-500/15 dark:text-sky-300',
  soon: 'bg-teal-500 text-white',
  now: 'bg-teal-500 text-white',
  met: 'bg-emerald-50 text-emerald-700 dark:!bg-emerald-500/15 dark:text-emerald-300',
  no_show: 'bg-rose-50 text-rose-700 dark:!bg-rose-500/15 dark:text-rose-300',
  canceled: 'bg-amber-50 text-amber-800 dark:!bg-amber-500/15 dark:text-amber-300',
};

// Phone or video (in-app meetings, 2026-10-25): every meeting says which,
// with its icon. An old Calendly meeting was a phone call.
const isVideo = (m) => m.kind === 'video';
const KindIcon = ({ m, size = 12, className }) =>
  React.createElement(isVideo(m) ? Video : Phone, {
    size,
    className: cn('shrink-0', className),
    'aria-label': isVideo(m) ? 'Video meeting' : 'Phone call',
  });

// Where a meeting stands right now, in plain words.
export const meetingStatus = (m, now) => {
  if (m.status === 'canceled') return { key: 'canceled', label: 'Canceled' };
  if (m.status === 'no_show' || m.noShowAt)
    return { key: 'no_show', label: 'No-show' };
  // Never "held" (Nic 2026-09-29): over and not flagged a no-show = met.
  if (m.outcome === 'held' || m.outcome === 'met') return { key: 'met', label: 'Met' };
  const mins = moment(m.startTime).diff(moment(now), 'minutes');
  if (mins > 15) return { key: 'upcoming', label: 'Upcoming' };
  if (mins > 0) return { key: 'soon', label: `In ${mins} min` };
  // Now = until the meeting's own end (Nic 2026-10-07: a 30-minute meeting
  // read "Now" for a full hour); no end on file = 30 minutes.
  const end = m.endTime
    ? moment(m.endTime)
    : moment(m.startTime).add(30, 'minutes');
  if (moment(now).isBefore(end)) return { key: 'now', label: 'Now' };
  return { key: 'met', label: 'Met' };
};

// Over, canceled or missed.
const isDone = (s) => ['met', 'no_show', 'canceled'].includes(s.key);
const isLive = (s) => s.key === 'soon' || s.key === 'now';
const toneOf = (m, s) => {
  if (isLunch(m)) return isDone(s) ? TONE.lunchPast : TONE.lunch;
  if (isOutlook(m)) return isDone(s) ? TONE.outlookPast : TONE.outlook;
  return TONE[s.key] || TONE.upcoming;
};

// The live dot: the one glyph worth its pixels.
const LiveDot = ({ s, className }) =>
  isLive(s) ? <span aria-label={s.label} className={cn('h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-current', className)} /> : null;
// The agenda's status: the word, in its color.
const StatusMark = ({ s }) => (
  <span className={cn('inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-semibold', CHIP[s.key] || CHIP.upcoming)}>
    <LiveDot s={s} />
    {s.label}
  </span>
);

const callable = (m, s) =>
  !!m.cadenceRunId &&
  m.status === 'active' &&
  (s.key === 'soon' ||
    s.key === 'now' ||
    s.key === 'upcoming');

// A video meeting still ahead or going on: Join, on either team's calendar
// (a client has no cadence run, so this doesn't lean on callable).
const joinable = (m, s) =>
  isVideo(m) && !!m.videoUrl && m.status === 'active' && ['soon', 'now', 'upcoming'].includes(s.key);

const IconButton = ({ title, onClick, children, disabled, primary }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={cn(
      'inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-xs font-semibold outline-none transition-[background-color] focus:outline-none disabled:cursor-not-allowed disabled:opacity-40',
      primary
        ? 'bg-emerald-600 text-white hover:bg-emerald-500'
        : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:!bg-white/10 dark:text-slate-200 dark:hover:!bg-white/15',
    )}
  >
    {children}
  </button>
);

// Who the meeting is with (Nic 2026-10-05): an initials circle, and the
// viewer's own meetings in their own color, so Everyone reads at a glance.
const initialsOf = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '?';
const isMineOf = (m, viewerUserId) => !!viewerUserId && m.hostUserId === viewerUserId;
// A meeting opens its lead (Sales) or its client (Client Onboarding).
const openableOf = (m) => !!(m.cadenceRunId || m.onboardingId);
const HostCircle = ({ m, mine }) => (
  <span
    title={m.hostName ? `With ${m.hostName}${mine ? ' (you)' : ''}` : undefined}
    className={cn(
      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
      mine
        ? 'bg-indigo-600 text-white'
        : 'bg-slate-200 text-slate-600 dark:!bg-white/10 dark:text-slate-300',
    )}
  >
    {initialsOf(m.hostName)}
  </span>
);

// ---- Agenda ----
const AgendaView = ({ meetings, now, showHost, onOpen, onCall, canCall, viewerUserId }) => {
  const days = useMemo(() => {
    const map = new Map();
    meetings.forEach((m) => {
      const key = moment(m.startTime).local().format('YYYY-MM-DD');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    });
    return [...map.entries()];
  }, [meetings]);

  if (!days.length) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 py-16 text-center">
        <CalendarDays
          size={28}
          className="text-slate-300 dark:text-slate-600"
        />
        <div className="text-sm font-semibold text-slate-600 dark:text-slate-300">
          No meetings in the next two weeks
        </div>
        <div className="text-xs text-slate-400">
          Meetings you book land here on their own.
        </div>
      </div>
    );
  }

  const today = moment(now).local();
  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 p-[16px] md:p-[24px]">
      {days.map(([key, list]) => {
        const d = moment(key, 'YYYY-MM-DD');
        const label = d.isSame(today, 'day')
          ? 'Today'
          : d.isSame(today.clone().add(1, 'day'), 'day')
            ? 'Tomorrow'
            : d.format('dddd');
        return (
          <section key={key}>
            <div className="mb-2 flex items-baseline gap-2 px-1">
              <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
                {label}
              </span>
              <span className="text-xs text-slate-400">
                {d.format('MMM D')}
              </span>
              <span className="ml-auto text-[11px] font-semibold tabular-nums text-slate-400">
                {(() => {
                  const n = list.filter((m) => !isOutlook(m) && !isLunch(m)).length;
                  return `${n} meeting${n === 1 ? '' : 's'}`;
                })()}
              </span>
            </div>
            <ul className="mb-0 list-none space-y-1.5 pl-0">
              {list.map((m) => {
                const s = meetingStatus(m, now);
                const start = moment(m.startTime).local();
                const mins = Math.max(
                  15,
                  moment(m.endTime).diff(moment(m.startTime), 'minutes'),
                );
                if (isLunch(m)) {
                  // Lunch (Nic 2026-11-02): a row of its own kind, like Outlook's.
                  return (
                    <li key={m.calendlyBookingId}>
                      <div className={cn('flex items-center gap-3 rounded-xl bg-violet-50/70 p-2 pr-3 ring-1 ring-violet-100 dark:!bg-violet-500/[0.08] dark:ring-violet-400/20', isDone(s) && 'opacity-60')}>
                        <span className={cn('flex w-16 shrink-0 flex-col items-center rounded-lg py-1.5 text-center', toneOf(m, s))}>
                          <span className="text-[15px] font-bold leading-none tabular-nums">{start.format('h:mm')}</span>
                          <span className="mt-1 text-[10px] font-semibold uppercase leading-none opacity-80">
                            {start.format('A')} · {mins}m
                          </span>
                        </span>
                        {showHost ? <HostCircle m={m} mine={isMineOf(m, viewerUserId)} /> : null}
                        <span className="min-w-0 flex-1">
                          <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
                            <Utensils size={13} className="shrink-0 text-violet-500" aria-label="Lunch" />
                            <span className="truncate">Lunch</span>
                          </span>
                          <span className="block truncate text-xs leading-[20px] text-slate-500 dark:text-slate-400">
                            <LunchTag />
                            {' · '}
                            {showHost && m.hostName ? `${m.hostName} · ` : ''}
                            {start.format('h:mm A')} to {moment(m.endTime).local().format('h:mm A')}
                          </span>
                        </span>
                      </div>
                    </li>
                  );
                }
                if (isOutlook(m)) {
                  return (
                    <li key={m.calendlyBookingId}>
                      <div className={cn('flex items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-2 pr-3 dark:!border-transparent dark:!bg-white/[0.04]', isDone(s) && 'opacity-60')}>
                        <span className={cn('flex w-16 shrink-0 flex-col items-center rounded-lg py-1.5 text-center', TONE.outlook)}>
                          <span className="text-[15px] font-bold leading-none tabular-nums text-slate-600 dark:text-slate-300">
                            {m.allDay ? 'All' : start.format('h:mm')}
                          </span>
                          <span className="mt-1 text-[10px] font-semibold uppercase leading-none text-slate-400">
                            {m.allDay ? 'day' : `${start.format('A')} · ${mins}m`}
                          </span>
                        </span>
                        {showHost ? <HostCircle m={m} mine={isMineOf(m, viewerUserId)} /> : null}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-slate-700 dark:text-slate-200">{nameOf(m)}</span>
                          <span className="block truncate text-xs leading-[20px] text-slate-500 dark:text-slate-400">
                            <OutlookTag />
                            {' · '}
                            {m.showAs === 'oof' ? 'Out of office' : m.showAs === 'tentative' ? 'Tentative' : 'Busy'}
                          </span>
                        </span>
                      </div>
                    </li>
                  );
                }
                return (
                  <li key={m.calendlyBookingId}>
                    <div
                      role={openableOf(m) ? 'button' : undefined}
                      tabIndex={openableOf(m) ? 0 : undefined}
                      onClick={() => openableOf(m) && onOpen(m)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && openableOf(m)) onOpen(m);
                      }}
                      className={cn(
                        'flex items-center gap-3 rounded-xl bg-white p-2 pr-3 shadow-[0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-slate-900/[0.04] outline-none dark:!bg-surface-card dark:ring-white/[0.06]',
                        openableOf(m)
                          ? 'cursor-pointer transition-[box-shadow] hover:shadow-[0_2px_4px_rgba(15,23,42,0.06),0_6px_16px_rgba(15,23,42,0.08)] focus-visible:ring-2 focus-visible:ring-sky-400/60'
                          : '',
                        // Canceled: faded (the color says the rest).
                        s.key === 'canceled' ? 'opacity-70' : '',
                        // Mine stands out while showing everyone.
                        showHost && isMineOf(m, viewerUserId)
                          ? 'shadow-[inset_3px_0_0_rgb(99,102,241)]'
                          : '',
                      )}
                    >
                      {/* The time, in the meeting's status color. */}
                      <span className={cn('flex w-16 shrink-0 flex-col items-center rounded-lg py-1.5 text-center', toneOf(m, s))}>
                        <span
                          className={cn(
                            'text-[15px] font-bold leading-none tabular-nums',
                            s.key === 'canceled' ? 'line-through' : '',
                          )}
                        >
                          {start.format('h:mm')}
                        </span>
                        <span className="mt-1 text-[10px] font-semibold uppercase leading-none opacity-80">
                          {start.format('A')} · {mins}m
                        </span>
                      </span>
                      {showHost ? <HostCircle m={m} mine={isMineOf(m, viewerUserId)} /> : null}
                      <span className="min-w-0 flex-1">
                        <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-slate-100">
                          <KindIcon m={m} size={13} className={isVideo(m) ? 'text-violet-500' : 'text-sky-500'} />
                          <span className={cn('truncate', s.key === 'canceled' ? 'line-through' : '')}>{nameOf(m)}</span>
                        </span>
                        <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                          {isVideo(m) ? 'Video meeting' : 'Phone call'}
                          {isLegacy(m) ? <LegacyTag className="ml-1.5" /> : null}
                          {' · '}
                          {m.leadPhone ? formatPhone(m.leadPhone) : 'No phone'}
                          {showHost && m.hostName
                            ? ` · with ${m.hostName}`
                            : ''}
                          {!openableOf(m) ? ' · not on the board' : ''}
                        </span>
                      </span>
                      <StatusMark s={s} />
                      {joinable(m, s) ? (
                        <a
                          href={m.videoUrl}
                          target="_blank"
                          rel="noreferrer"
                          title={`Join the video meeting with ${nameOf(m)}`}
                          className={cn(
                            'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold no-underline',
                            s.key === 'soon' || s.key === 'now'
                              ? 'bg-violet-600 text-white hover:bg-violet-500 hover:text-white'
                              : 'text-violet-700 ring-1 ring-violet-200 hover:bg-violet-50 dark:text-violet-300 dark:ring-violet-400/30 dark:hover:!bg-violet-500/10',
                          )}
                        >
                          <Video size={13} />
                          <span className="hidden sm:inline">Join</span>
                        </a>
                      ) : callable(m, s) && (!canCall || canCall(m)) ? (
                        <IconButton
                          title={`Call ${nameOf(m)}`}
                          primary={s.key === 'soon' || s.key === 'now'}
                          onClick={() => onCall(m)}
                        >
                          <Phone size={13} />
                          <span className="hidden sm:inline">Call now</span>
                        </IconButton>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

// A block is never drawn shorter than this, so it stays readable...
const MIN_BLOCK_PX = 22;
// ...which is how many minutes it covers on the grid. The lanes go by the
// DRAWN length (QA 2026-10-28: a 15-minute meeting at 9:15 was judged clear
// of 9:30 and drew over it).
const MIN_BLOCK_MIN = Math.ceil((MIN_BLOCK_PX + 2) / (HOUR_PX / 60));
const drawnEnd = (m) => Math.max(new Date(m.endTime).getTime(), new Date(m.startTime).getTime() + MIN_BLOCK_MIN * 60000);

// Side-by-side lanes for meetings that overlap on the same day.
export const layoutLanes = (list) => {
  const sorted = [...list].sort(
    (a, b) => new Date(a.startTime) - new Date(b.startTime),
  );
  const out = [];
  let group = [];
  let groupEnd = 0;
  const flush = () => {
    const lanes = [];
    group.forEach((m) => {
      const s = new Date(m.startTime).getTime();
      let lane = lanes.findIndex((end) => end <= s);
      if (lane < 0) lane = lanes.length;
      lanes[lane] = drawnEnd(m);
      out.push({ m, lane });
    });
    const count = lanes.length;
    out.slice(out.length - group.length).forEach((x) => {
      // eslint-disable-next-line no-param-reassign
      x.count = count;
    });
    group = [];
  };
  sorted.forEach((m) => {
    const s = new Date(m.startTime).getTime();
    if (group.length && s >= groupEnd) flush();
    group.push(m);
    groupEnd = Math.max(groupEnd, drawnEnd(m));
  });
  if (group.length) flush();
  return out;
};

// ---- Week ----
// What a block has room for (Nic 2026-10-28): the glyph, the name and the
// time; the time alone; or just its color.
const FULL_PX = 70;
const TIME_PX = 48;

// dayCount: 7, or 3 on a phone (the columns stay as wide as on a desktop).
const WeekView = ({ weekStart, dayCount = 7, meetings, now, showHost, onOpen, viewerUserId }) => {
  const days = [...Array(dayCount)].map((_, i) => weekStart.clone().add(i, 'days'));
  // The grid's width, for what each block can show.
  const gridRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(0);
  useEffect(() => {
    const measure = () => setGridWidth(gridRef.current ? gridRef.current.clientWidth : 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [dayCount]);
  // The hour gutter is 3.25rem (52px).
  const colWidth = gridWidth ? (gridWidth - 52) / dayCount : 999;
  // Hours shown: 7 AM to 8 PM, stretched to fit any meeting outside it
  // (an all-day Outlook item sits above the hours, so it never stretches).
  const hours = meetings.filter((m) => !m.allDay).reduce(
    (acc, m) => {
      const s = moment(m.startTime).local();
      const e = moment(m.endTime).local();
      return [Math.min(acc[0], s.hour()), Math.max(acc[1], e.hour() + 1)];
    },
    [7, 20],
  );
  const [firstHour, lastHour] = [hours[0], Math.min(24, hours[1])];
  const rows = [...Array(lastHour - firstHour)].map((_, i) => firstHour + i);
  const today = moment(now).local();
  const nowTop =
    ((today.hour() - firstHour) * 60 + today.minute()) * (HOUR_PX / 60);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="grid border-b border-slate-200/70 bg-white pr-2 dark:border-white/10 dark:!bg-surface-card"
        style={{ gridTemplateColumns: `3.25rem repeat(${dayCount}, minmax(0, 1fr))` }}
      >
        <span />
        {days.map((d) => {
          const isToday = d.isSame(today, 'day');
          return (
            <div key={d.format('YYYY-MM-DD')} className="py-2 text-center">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                {d.format('ddd')}
              </div>
              <div
                className={cn(
                  'mx-auto mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold tabular-nums',
                  isToday
                    ? 'bg-sky-500 text-white'
                    : 'text-slate-700 dark:text-slate-200',
                )}
              >
                {d.format('D')}
              </div>
            </div>
          );
        })}
      </div>
      <div className="os-scroll min-h-0 flex-1 overflow-y-auto pr-2">
        <div
          ref={gridRef}
          className="relative grid"
          style={{ height: rows.length * HOUR_PX, gridTemplateColumns: `3.25rem repeat(${dayCount}, minmax(0, 1fr))` }}
        >
          <div className="relative">
            {rows.map((h, i) => (
              <span
                key={h}
                style={{ top: i * HOUR_PX - 6 }}
                className="absolute right-2 text-[10px] font-semibold tabular-nums text-slate-400"
              >
                {i === 0 ? '' : moment().hour(h).minute(0).format('h A')}
              </span>
            ))}
          </div>
          {days.map((d) => {
            const key = d.format('YYYY-MM-DD');
            const list = meetings.filter((m) =>
              moment(m.startTime).local().isSame(d, 'day'),
            );
            const allDay = list.filter((m) => m.allDay);
            const timed = list.filter((m) => !m.allDay);
            const isToday = d.isSame(today, 'day');
            return (
              <div
                key={key}
                className={cn(
                  'relative border-l border-slate-200/70 dark:border-white/10',
                  isToday ? 'bg-sky-50/40 dark:!bg-sky-500/[0.04]' : '',
                )}
              >
                {rows.map((h, i) => (
                  <span
                    key={h}
                    style={{ top: i * HOUR_PX }}
                    className="absolute inset-x-0 border-t border-slate-200/60 dark:border-white/[0.06]"
                  />
                ))}
                {isToday && nowTop >= 0 && nowTop <= rows.length * HOUR_PX ? (
                  <span
                    style={{ top: nowTop }}
                    className="absolute inset-x-0 z-10 h-0.5 bg-red-500"
                  >
                    <span className="absolute -left-1 -top-[3px] h-2 w-2 rounded-full bg-red-500" />
                  </span>
                ) : null}
                {/* All-day Outlook items: a thin strip at the top of the day. */}
                {allDay.map((m, i) => (
                  <span
                    key={m.calendlyBookingId}
                    title={`Outlook, all day: ${nameOf(m)}`}
                    style={{ top: 2 + i * 20 }}
                    className={cn(
                      'absolute inset-x-[2px] z-[6] h-[18px] truncate rounded px-1.5 text-[10px] font-bold leading-[16px]',
                      d.isBefore(today, 'day') ? TONE.outlookPast : TONE.outlook,
                    )}
                  >
                    {nameOf(m)}
                  </span>
                ))}
                {layoutLanes(timed).map(({ m, lane, count }) => {
                  const s = moment(m.startTime).local();
                  const mins = Math.max(
                    20,
                    moment(m.endTime).diff(moment(m.startTime), 'minutes'),
                  );
                  const top =
                    ((s.hour() - firstHour) * 60 + s.minute()) * (HOUR_PX / 60);
                  const st = meetingStatus(m, now);
                  const width = 100 / count;
                  // Outlook and lunch blocks: no live dot, no kind icon.
                  const outlook = isOutlook(m) || isLunch(m);
                  const blockWidth = (colWidth * width) / 100 - 4;
                  // Room for: everything, the time alone, or only the color.
                  const show = blockWidth >= FULL_PX ? 'full' : blockWidth >= TIME_PX ? 'time' : 'bar';
                  return (
                    <button
                      key={m.calendlyBookingId}
                      type="button"
                      disabled={!openableOf(m)}
                      onClick={() => onOpen(m)}
                      title={
                        isLunch(m)
                          ? `Lunch${showHost && m.hostName ? `: ${m.hostName}` : ''}, ${s.format('h:mm A')} to ${moment(m.endTime).local().format('h:mm A')}`
                          : outlook
                            ? `Outlook: ${nameOf(m)}, ${s.format('h:mm A')}`
                            : `${isVideo(m) ? 'Video meeting' : 'Phone call'}: ${nameOf(m)}, ${s.format('h:mm A')}${showHost && m.hostName ? ` with ${m.hostName}` : ''} (${st.label})`
                      }
                      style={{
                        top: top + 1,
                        height: Math.max(MIN_BLOCK_PX, mins * (HOUR_PX / 60) - 2),
                        left: `calc(${lane * width}% + 2px)`,
                        width: `calc(${width}% - 4px)`,
                      }}
                      className={cn(
                        'absolute z-[5] overflow-hidden rounded-md text-left text-[11px] leading-tight shadow-sm outline-none focus:outline-none disabled:cursor-default whitespace-nowrap',
                        show === 'bar' ? 'px-0 py-0' : 'px-1.5 py-1',
                        toneOf(m, st),
                        st.key === 'canceled' ? 'line-through' : '',
                        showHost && isMineOf(m, viewerUserId) ? 'ring-2 ring-indigo-500' : '',
                      )}
                    >
                      {/* One line, time right-aligned (Nic 2026-09-24): a
                          second line hid under short blocks. */}
                      {show === 'bar' ? null : (
                        <span className="flex items-center gap-1.5">
                          {outlook ? null : <LiveDot s={st} />}
                          {show === 'full' ? (
                            <>
                              {outlook ? null : <KindIcon m={m} size={11} className="opacity-90" />}
                              <span className="min-w-0 flex-1 truncate font-bold">
                                {showHost ? `${initialsOf(m.hostName)} · ` : ''}
                                {nameOf(m)}
                              </span>
                            </>
                          ) : null}
                          <span className={cn('shrink-0 tabular-nums', show === 'full' ? 'opacity-85' : 'font-bold')}>
                            {s.format('h:mm A')}
                          </span>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Under 640px (Tailwind's sm): a phone.
const NARROW_QUERY = '(max-width: 639px)';
const useNarrow = () => {
  const [narrow, setNarrow] = useState(() => (window.matchMedia ? window.matchMedia(NARROW_QUERY).matches : false));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = (e) => setNarrow(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return narrow;
};

const VIEW_KEY = 'os-meetings-view';
const readView = () => {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'week' ? 'week' : 'agenda';
  } catch (_) {
    return 'agenda';
  }
};
// Show or hide their Outlook items (Nic 2026-10-28), remembered on this
// browser like the view. On by default.
const OUTLOOK_KEY = 'os-meetings-outlook';
const readShowOutlook = () => {
  try {
    return window.localStorage.getItem(OUTLOOK_KEY) !== 'off';
  } catch (_) {
    return true;
  }
};

const MeetingsCalendar = ({
  open,
  onClose,
  now,
  canManage,
  onOpen,
  onCall,
  // Ownership rule (Nic 2026-09-29): Call now only on the viewer's own
  // leads/clients. The page decides; null = every callable meeting.
  canCall = null,
  apiBase = '/onboarding-board',
  // Your own meetings stand out while showing everyone (2026-10-05).
  viewerUserId = null,
  // Managers, admins and view only (Nic 2026-10-05, both pages): always
  // everyone, filtered by clicking the host circles like the boards. Anyone
  // else sees their own (no Mine / Everyone toggle since Nic 2026-10-11).
  seesAll = false,
}) => {
  const [view, setView] = useState(readView);
  const [showOutlook, setShowOutlook] = useState(readShowOutlook);
  useEffect(() => {
    try {
      window.localStorage.setItem(OUTLOOK_KEY, showOutlook ? 'on' : 'off');
    } catch (_) {
      // ignore
    }
  }, [showOutlook]);
  const scope = seesAll ? 'all' : 'mine';
  const [hiddenHosts, setHiddenHosts] = useState(() => new Set());
  const toggleHost = (id) =>
    setHiddenHosts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // A phone shows three days from today, a desktop the whole week (Nic
  // 2026-10-28: seven columns in 450px left room for nothing).
  const narrow = useNarrow();
  const dayCount = narrow ? 3 : 7;
  const anchor = () => (narrow ? moment().local().startOf('day') : moment().local().startOf('week'));
  const [weekStart, setWeekStart] = useState(anchor);
  useEffect(() => {
    setWeekStart(anchor());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrow]);
  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_KEY, view);
    } catch (_) {
      // ignore
    }
  }, [view]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const range = useMemo(() => {
    if (view === 'week') {
      return {
        from: weekStart.toISOString(),
        to: weekStart.clone().add(dayCount, 'days').toISOString(),
      };
    }
    const start = moment().local().startOf('day');
    return {
      from: start.toISOString(),
      to: start.clone().add(15, 'days').toISOString(),
    };
    // A new day re-anchors the agenda; minute ticks do not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, weekStart, moment(now).local().format('YYYY-MM-DD')]);

  const q = useFetchMeetings({ ...range, scope, apiBase }, { enabled: !!open });
  // The viewer's own Outlook appointments ride along (Nic 2026-10-28), as
  // rows of their own kind: never opened, never called, never alerted.
  const outlookCount = q.data?.outlook?.events?.length || 0;
  // Lunch (Nic 2026-11-02): each host's lunch per day, blocks of their own
  // kind like the Outlook rows: never opened, never called, never alerted.
  const allMeetings = useMemo(
    () => [
      ...(q.data?.meetings || []),
      ...(showOutlook ? q.data?.outlook?.events || [] : []).map((e) => ({
        ...e,
        calendlyBookingId: `outlook:${e.id}`,
        provider: 'outlook',
        status: 'active',
      })),
      ...(q.data?.lunch?.blocks || []).map((b) => ({
        ...b,
        calendlyBookingId: `lunch:${b.id}`,
        provider: 'lunch',
        status: 'active',
        allDay: false,
      })),
    ],
    [q.data, showOutlook],
  );
  // Hosts on screen (for the circles), then the click filter.
  // Lunch is not a meeting (review 2026-11-03): the chips count without it.
  const hosts = [];
  const seenHost = new Map();
  allMeetings.filter((m) => !isLunch(m)).forEach((m) => {
    const id = m.hostUserId || `name:${m.hostName || ''}`;
    if (!seenHost.has(id)) {
      seenHost.set(id, { id, name: m.hostName || 'Unknown', count: 0 });
      hosts.push(seenHost.get(id));
    }
    seenHost.get(id).count += 1;
  });
  hosts.sort((a, b) => a.name.localeCompare(b.name));
  const hostIdOf = (m) => m.hostUserId || `name:${m.hostName || ''}`;
  const meetings = seesAll
    ? allMeetings.filter((m) => !hiddenHosts.has(hostIdOf(m)))
    : allMeetings;

  if (!open) return null;

  const thisWeek = anchor();
  const lastDay = weekStart.clone().add(dayCount - 1, 'days');
  const weekLabel = `${weekStart.format('MMM D')} - ${lastDay.format(
    weekStart.month() === lastDay.month() ? 'D' : 'MMM D',
  )}`;

  return createPortal(
    <div className="fixed inset-0 z-[1060]">
      <div
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute inset-2 flex flex-col overflow-hidden rounded-2xl bg-[#F5F7FA] shadow-2xl dark:!bg-surface-page md:inset-6">
        <div className="flex flex-wrap items-center gap-2 bg-white px-4 py-3 shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:!bg-surface-elevated">
          <div className="flex min-w-0 items-center gap-2 text-base font-bold text-slate-800 dark:text-slate-100">
            <CalendarDays size={17} className="shrink-0 text-slate-400" />
            <span className="truncate">Meetings</span>
            {q.isFetching ? (
              <Loader2 size={14} className="animate-spin text-slate-400" />
            ) : null}
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {seesAll && hosts.length ? (
              // Everyone's meetings: a circle per host, click to hide or
              // show theirs (like the boards).
              <div className="flex items-center gap-1.5">
                {hosts.map((h) => {
                  const off = hiddenHosts.has(h.id);
                  const mine = !!viewerUserId && h.id === viewerUserId;
                  return (
                    <button
                      key={h.id}
                      type="button"
                      onClick={() => toggleHost(h.id)}
                      aria-pressed={!off}
                      title={`${h.name}: ${h.count} meeting${h.count === 1 ? '' : 's'}\n${
                        off ? 'Hidden. Click to show again' : 'Click to hide their meetings'
                      }`}
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-bold outline-none ring-2 ring-white transition-opacity focus:outline-none dark:ring-surface-elevated',
                        mine
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-800 text-white dark:bg-slate-200 dark:!text-slate-900',
                        off && 'opacity-30',
                      )}
                    >
                      {initialsOf(h.name)}
                    </button>
                  );
                })}
              </div>
            ) : null}
            {view === 'week' ? (
              <div className="flex items-center gap-1">
                {!weekStart.isSame(thisWeek, 'day') ? (
                  <button
                    type="button"
                    onClick={() => setWeekStart(thisWeek)}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-200 dark:!bg-white/10 dark:text-slate-400 dark:hover:!bg-white/15 whitespace-nowrap"
                  >
                    {narrow ? 'Today' : 'This week'}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() =>
                    setWeekStart((w) => w.clone().subtract(dayCount, 'days'))
                  }
                  title={narrow ? 'Previous days' : 'Previous week'}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:!bg-white/10"
                >
                  <ChevronLeft size={15} />
                </button>
                <span className="w-32 text-center text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                  {weekLabel}
                </span>
                <button
                  type="button"
                  onClick={() => setWeekStart((w) => w.clone().add(dayCount, 'days'))}
                  title={narrow ? 'Next days' : 'Next week'}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:!bg-white/10"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            ) : null}

            <div className="inline-flex rounded-lg bg-slate-100 p-0.5 dark:!bg-white/10">
              {[
                ['agenda', 'Agenda', List],
                ['week', 'Week', CalendarDays],
              ].map(([k, l, Icon]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setView(k)}
                  className={cn(
                    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-semibold outline-none focus:outline-none',
                    view === k
                      ? 'bg-slate-900 text-white dark:!bg-slate-100 dark:!text-slate-900'
                      : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white',
                  )}
                >
                  <Icon size={13} />
                  {l}
                </button>
              ))}
            </div>

            {/* Their Outlook items, on or off (Nic 2026-10-28). Only once
                Outlook is connected; the count says what's hidden. */}
            {q.data?.outlook?.configured ? (
              <button
                type="button"
                onClick={() => setShowOutlook((v) => !v)}
                aria-pressed={showOutlook}
                aria-label={showOutlook ? 'Hide your Outlook appointments' : 'Show your Outlook appointments'}
                title={showOutlook ? 'Hide your Outlook appointments' : 'Show your Outlook appointments'}
                className={cn(
                  'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-xs font-semibold outline-none focus:outline-none',
                  showOutlook
                    ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:!bg-white/10 dark:text-slate-200 dark:hover:!bg-white/15'
                    : 'text-slate-400 ring-1 ring-slate-200 hover:text-slate-600 dark:text-slate-500 dark:ring-white/15 dark:hover:text-slate-300',
                )}
              >
                {showOutlook ? <Eye size={13} /> : <EyeOff size={13} />}
                Outlook
                {!showOutlook && outlookCount ? <span className="tabular-nums opacity-70">({outlookCount})</span> : null}
              </button>
            ) : null}

            <button
              type="button"
              onClick={onClose}
              title="Close"
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:!bg-white/10"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        {/* What the colors and marks mean (Nic 2026-10-28). */}
        <div
          aria-label="Legend"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-white px-4 py-1.5 text-[10.5px] font-medium text-slate-400 dark:!bg-surface-elevated dark:text-slate-500"
        >
          {[
            ['Upcoming', 'bg-sky-500'],
            ['Now', 'bg-teal-500'],
            ['Met', 'bg-emerald-200 dark:!bg-emerald-500/40'],
            ['No-show', 'bg-rose-200 dark:!bg-rose-500/40'],
            ['Canceled', 'bg-amber-200 dark:!bg-amber-500/40'],
          ].map(([word, swatch]) => (
            <span key={word} className={cn('inline-flex items-center gap-1.5', word === 'Canceled' && 'line-through')}>
              <i className={cn('inline-block h-2.5 w-2.5 rounded-sm', swatch)} /> {word}
            </span>
          ))}
          {q.data?.outlook?.configured ? (
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-2.5 w-2.5 rounded-sm border border-dashed border-slate-400 bg-slate-100 dark:!border-transparent dark:!bg-white/10" /> Outlook
            </span>
          ) : null}
          {q.data?.lunch?.blocks?.length ? (
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-2.5 w-2.5 rounded-sm bg-violet-200 dark:!bg-violet-500/40" /> Lunch
            </span>
          ) : null}
        </div>

        {q.isError ? (
          <div className="p-6 text-center text-sm text-red-600 dark:text-red-400">
            Meetings could not be loaded. Try again in a moment.
          </div>
        ) : view === 'week' ? (
          <WeekView
            weekStart={weekStart}
            dayCount={dayCount}
            meetings={meetings}
            now={now}
            showHost={scope === 'all'}
            onOpen={onOpen}
            viewerUserId={viewerUserId}
          />
        ) : (
          <div className="os-scroll min-h-0 flex-1 overflow-y-auto">
            {q.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 size={20} className="animate-spin text-slate-400" />
              </div>
            ) : (
              <AgendaView
                meetings={meetings}
                now={now}
                showHost={scope === 'all'}
                onOpen={onOpen}
                onCall={onCall}
                canCall={canCall}
                viewerUserId={viewerUserId}
              />
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default MeetingsCalendar;
